import {
  type ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { readStoredChoice, writeStored } from '#client/lib/storage';

/**
 * Colour theme and table density — the two persisted UI preferences.
 *
 * They live in one provider because they are one concern (per-user display
 * settings), share one storage mechanism, and are both applied before first
 * paint by the same script: public/theme-init.js. Splitting them would mean two
 * contexts and two pre-paint reads for no gain.
 *
 * ## Why this is written the way it is
 *
 * The React Compiler lint rules reject "copy external data into state from an
 * effect", which is exactly what the obvious implementations of both features
 * look like:
 *
 *     useEffect(() => setMode(localStorage.getItem('ndc.theme')), [])       // no
 *     useEffect(() => media.addEventListener('change', () => setDark(...))) // no
 *
 * So instead: localStorage is read once in a lazy `useState` initialiser, and
 * the OS preference is read through `useSyncExternalStore`, which is the
 * purpose-built primitive for an external mutable source. Both also get the
 * right value on the very first render, which means React agrees with what
 * theme-init.js already painted and there is no flash.
 *
 * The one `useEffect` that remains performs DOM and storage *writes*. That is a
 * genuine side effect, not derived state, and is correct.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';
export type Density = 'compact' | 'default' | 'comfortable';

const THEME_KEY = 'ndc.theme';
const DENSITY_KEY = 'ndc.density';

export const THEME_LABELS = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
} as const satisfies Record<ThemeMode, string>;

export const DENSITY_LABELS = {
  compact: 'Compact',
  default: 'Default',
  comfortable: 'Comfortable',
} as const satisfies Record<Density, string>;

/*
 * Module scope: one media-query listener for the whole app, subscribed through
 * useSyncExternalStore rather than an effect.
 */
const media =
  typeof window === 'undefined' ? null : window.matchMedia('(prefers-color-scheme: dark)');

function subscribeSystemTheme(onChange: () => void): () => void {
  media?.addEventListener('change', onChange);
  return () => media?.removeEventListener('change', onChange);
}

const getSystemDark = (): boolean => media?.matches ?? false;

/** Server snapshot. There is no SSR here, but the hook requires one. */
const getSystemDarkFallback = (): boolean => false;

const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];

/**
 * Pins a subtree to the light palette regardless of the app theme.
 *
 * Used by the print preview: a rendered PDF is always a white A4 page, and dark
 * chrome pressed against it looks broken. It works because semantic.css
 * declares every role on `:root, .light`, so putting this class on an element
 * re-declares them there and every descendant inherits.
 *
 * It is also the acceptance test for `@theme inline` in tailwind-theme.css.
 * Without `inline`, Tailwind resolves its theme variables once at `:root` and
 * this subtree would render dark inside a dark app — the failure is invisible
 * everywhere else, so if that subtree ever looks wrong, start there.
 *
 * A named constant rather than a bare string so it cannot be mistaken for a
 * stray class and removed.
 */
export const LIGHT_SCOPE = 'light';
const DENSITIES: readonly Density[] = ['compact', 'default', 'comfortable'];

interface ThemeState {
  /** What the user chose, which may be `system`. */
  mode: ThemeMode;
  /** What that actually resolves to right now. */
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  density: Density;
  setDensity: (density: Density) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    readStoredChoice(THEME_KEY, { allowed: THEME_MODES, fallback: 'system' }),
  );
  const [density, setDensityState] = useState<Density>(() =>
    readStoredChoice(DENSITY_KEY, { allowed: DENSITIES, fallback: 'default' }),
  );

  const systemDark = useSyncExternalStore(
    subscribeSystemTheme,
    getSystemDark,
    getSystemDarkFallback,
  );

  const resolved: ResolvedTheme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  // Side effects only: paint the DOM and persist the choice. Nothing here
  // derives state, which is what keeps the compiler lint happy.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    // Keeps native scrollbars, selects and autofill in step with the theme.
    root.style.colorScheme = resolved;
    writeStored(THEME_KEY, mode);
  }, [mode, resolved]);

  useEffect(() => {
    document.documentElement.dataset.density = density;
    writeStored(DENSITY_KEY, density);
  }, [density]);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
  const setDensity = useCallback((next: Density) => setDensityState(next), []);

  const value = useMemo<ThemeState>(
    () => ({ mode, resolved, setMode, density, setDensity }),
    [mode, resolved, setMode, density, setDensity],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeState {
  const context = use(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
}
